import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	inject,
	OnInit
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { HlrCurrencies, Source, SourcePayload } from '@helpers/types/models';
import { SourcesService } from '../sources.service';
import { SourceFormService } from './source-form.service';
import { AlarisDialogService,
	AlarisLanguageService,
	AlarisLoadingDirective } from '@campaign-portal/components-library';
import { AlarisTabsButtonComponent } from '@campaign-portal/components-library';
import { ConfirmCloseDialogComponent } from '../dialog-close/dialog-close.component';
import { ConnectionType } from '@helpers/types/models';
import { MnpCreateUpdateResponse } from '@helpers/types/api';
import { DsInputComponent } from '@alarislabs/angular-components/input';
import { DsSelectComponent } from '@alarislabs/angular-components/select';
import { DsCheckboxComponent } from '@alarislabs/angular-components/checkbox';
import { DsButtonComponent } from '@alarislabs/angular-components/button';
import { DsSelectDropdownPresetComponent } from '@alarislabs/angular-components/dropdown';
import { DsBadgeIconComponent } from '@alarislabs/angular-components/badge-icon';
import { EntityNotificationService } from '@helpers/services/entity-notification.service';

@Component({
	selector: 'app-edit-source',
	templateUrl: './edit-source.component.html',
	styleUrls: ['./edit-source.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		TranslateModule,
		AlarisLoadingDirective,
		AlarisTabsButtonComponent,
		DsInputComponent,
		DsSelectComponent,
		DsCheckboxComponent,
		DsButtonComponent,
		DsSelectDropdownPresetComponent,
		DsBadgeIconComponent
	],
	standalone: true
})
export class EditSourceComponent implements OnInit {
	private entityNotification = inject(EntityNotificationService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly sourcesService = inject(SourcesService);
	private readonly dialog = inject(AlarisDialogService);
	private readonly lService = inject(AlarisLanguageService);
	private readonly destroyRef = inject(DestroyRef);

	readonly form = inject(SourceFormService);

	readonly errors = this.form.errors;
	source!: Source;

	ngOnInit(): void {
		const id = this.route.snapshot.paramMap.get('id');
		let source: Source;
		if (id) {
			const cached = this.sourcesService.loadFromSession();
			source = cached && cached.sourceId === Number(id) ? cached : this.form.getDefaultSource();
		} else {
			source = this.form.getDefaultSource();
		}
		this.source = source;
		this.form.init(source);
	}

	save(): void {
		const payload: SourcePayload = this.form.getPayload();
		if ( !this.form.hasHuntstopField() ) {
			payload.huntStop = null;
		}

		if (
			(payload.connectionType === ConnectionType.HTTP ||
				payload.connectionType === ConnectionType.SS7) &&
			payload.huntStop !== 'DISABLED'
		) {
			payload.requestTimeout = payload.requestTimeout ?? this.form.defaultRequestTimeout;
		}

		this.sourcesService
			.update(payload)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp: MnpCreateUpdateResponse) => {
				const success = !!(resp.createdId || resp.success);
				const create = payload.sourceId === null;
				if (success) {
					this.sourcesService.clearSession();
					this.entityNotification.showSuccess('sources.source', create ? 'created' : 'updated');
					this.router.navigate(['/sources']);
				}
			});
	}

	close(): void {
		if (!this.form.sourceForm.dirty) {
			this.router.navigate(['/sources']);
			return;
		}
		const message = this.lService.translate('sources.close_confirmation');
		const dialogRef = this.dialog.open<boolean>(ConfirmCloseDialogComponent, {
			autoFocus: false,
			data: { message },
		});
		dialogRef.closed
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: boolean | undefined) => {
				if (result) {
					this.router.navigate(['/sources']);
				}
			});
	}

	getIconName = (currency: HlrCurrencies): string =>
		currency?.currencyIconName ?? '';
}